// DropBox.js
import React, { useRef, useState, useEffect } from 'react';
import { simpleReq } from "../../../utils/globalutils.js";
import imageBtnIcone from "../../../assets/image/imageicone.png";
import PdfImage from "../../../assets/image/pdf.png";
import Spinner from "../loading/spinner.js";

function DropBox({ onFileIsUploaded, fileUrl, removeFileUrl }) {
  const fileInput = useRef(null);
  const containerRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
  
  const handleClick = () => {
    fileInput.current.click();
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    uploadFiles(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    uploadFiles(files);
  };

  const handleContextMenu = (e) => {
    // if right-click inside the dropbox, show custom menu with 'Coller'
    e.preventDefault();
    setMenuPos({ x: e.clientX, y: e.clientY });
    setMenuVisible(true);
  };

  useEffect(() => {
    const onDocClick = (ev) => {
      if (menuVisible) setMenuVisible(false);
    };
    window.addEventListener('click', onDocClick);
    return () => window.removeEventListener('click', onDocClick);
  }, [menuVisible]);

  // Auto-focus and force focus back when blurred
  useEffect(() => {
    if (containerRef.current && typeof containerRef.current.focus === 'function') {
      try { containerRef.current.focus(); } catch (e) { /* ignore */ }
    }
  }, []);

  const handleBlur = (e) => {
    // Re-focus shortly after blur to 'force' focus on the dropbox
    // Use setTimeout to allow context menu clicks to register first
    setTimeout(() => {
      if (containerRef.current && typeof containerRef.current.focus === 'function') {
        try { containerRef.current.focus(); } catch (err) { /* ignore */ }
      }
    }, 0);
  };

  const pasteFromClipboard = async () => {
    setMenuVisible(false);
    // Try to read clipboard items (images/files)
    try {
      if (navigator.clipboard && navigator.clipboard.read) {
        const items = await navigator.clipboard.read();
        const files = [];
        for (const item of items) {
          for (const type of item.types) {
            if (type.startsWith('image/')) {
              const blob = await item.getType(type);
              const filename = `pasted-image.${type.split('/')[1] || 'png'}`;
              const file = new File([blob], filename, { type: blob.type });
              files.push(file);
            }
            // if item provides a file type directly
            if (type === 'application/octet-stream') {
              const blob = await item.getType(type);
              const file = new File([blob], 'pasted-file', { type: blob.type });
              files.push(file);
            }
          }
        }
        if (files.length > 0) {
          uploadFiles(files);
          return;
        }
      }

      // Fallback: try readText and check for data URL
      if (navigator.clipboard && navigator.clipboard.readText) {
        const txt = await navigator.clipboard.readText();
        if (txt && txt.startsWith('data:image')) {
          // convert dataURL to blob
          const res = await fetch(txt);
          const blob = await res.blob();
          const ext = blob.type.split('/')[1] || 'png';
          const file = new File([blob], `pasted-image.${ext}`, { type: blob.type });
          uploadFiles([file]);
          return;
        }
      }

      // If nothing found, optionally inform user
      console.log('No image/file found in clipboard or browser does not support navigator.clipboard.read()');
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
      // In some browsers permissions or insecure context will block this
    }
  };

  const handlePaste = (e) => {
    try {
      const items = e.clipboardData && e.clipboardData.items;
      if (!items) return;
      const files = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) files.push(file);
        } else if (item.type && item.type.indexOf('image') === 0) {
          // some browsers expose image types as non-file items
          const blob = item.getAsFile && item.getAsFile();
          if (blob) files.push(blob);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        uploadFiles(files);
      }
    } catch (err) {
      // ignore paste errors
      console.error('Paste handling error', err);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const uploadFiles = async (files) => {
    setLoading(true);
    try {
      await Promise.all(files.map((file) => uploadFile(file)));
    } catch (error) {
      console.error("Error uploading files:", error);
    }
    setLoading(false);
  };

  const uploadFile = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
  
      fileReader.onload = async () => {
        try {
          const isHttps = window.location.protocol === "https:";
          const baseUrl = isHttps ? "https://api.kojyto.fr:8125" : "http://api.kojyto.fr:8126";
  
          const d = await simpleReq(baseUrl + "/upload", file);
          const fileUrl = baseUrl + "/" + d.path;
  
          onFileIsUploaded(fileUrl);
          fileInput.current.value = '';
          resolve();
        } catch (err) {
          reject(err);
        }
      };
  
      fileReader.onerror = (error) => reject(error);
      fileReader.readAsArrayBuffer(file);
    });
  };

  const getUrlForExtension = (url) => {
    const extension = url.split('.').pop().toLowerCase();
    return extension === "pdf" ? PdfImage : url;
  };

  return (
    <>
      {/* Zone de dépôt de fichiers */}
      <div
        className="flex w-full flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-100 cursor-pointer h-32"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        onPaste={handlePaste}
        tabIndex={0}
        ref={containerRef}
        onBlur={handleBlur}
      ><p className="text-gray-500 text-center">
        { 
          loading
          ? <div className="flex gap-3"><span>Chargement</span> <Spinner /></div>
          : 
            'Glissez et déposez vos fichiers ici ou cliquez pour sélectionner'
         
         } </p>
        <input
          ref={fileInput}
          onChange={handleFileSelect}
          type="file"
          id="fileElem"
          accept={"image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/rtf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx,.md"}
          className="hidden"
        />
      </div>
      {menuVisible && (
        <div style={{position: 'fixed', top: menuPos.y, left: menuPos.x, zIndex: 9999}}>
          <div className="bg-white border rounded shadow-md py-1" style={{minWidth: 120}}>
            <button className="block w-full text-left px-3 py-2 hover:bg-gray-100" onClick={(e)=>{e.stopPropagation(); pasteFromClipboard();}}>Coller</button>
            <button className="block w-full text-left px-3 py-2 hover:bg-gray-100" onClick={(e)=>{e.stopPropagation(); setMenuVisible(false);}}>Fermer</button>
          </div>
        </div>
      )}
    </>
  );
}

export default DropBox;
